import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { DRAG_THRESHOLD, capture, isMac, prefersReducedMotion, swallowNextClick } from './events';
import { boundsOf, clampZoom, fitViewport, screenToFlow, zoomAt } from './geometry';
import type { Box, Viewport, XY } from './geometry';
import type { CanvasStore } from './canvasStore';

/** Background dot grid, in flow units. */
const GAP = 20;
const DOT_R = 0.8;
const ANIMATE_MS = 200;
/** Wheel has no end event: a gesture ends after this long without one. */
const WHEEL_IDLE_MS = 150;

interface Refs {
  pane: React.RefObject<HTMLDivElement>;
  layer: React.RefObject<HTMLDivElement>;
  pattern: React.RefObject<SVGPatternElement>;
  dot: React.RefObject<SVGCircleElement>;
}

type Gesture =
  | { kind: 'pan'; id: number; start: XY; vp0: Viewport; moved: boolean }
  | { kind: 'pinch'; vp0: Viewport; mid0: XY; dist0: number }
  | null;

export interface ViewportApi {
  /** The live viewport. The store holds the value as of the last gesture end. */
  vp: React.MutableRefObject<Viewport>;
  /** Client coordinates to pane-relative ones. */
  panePoint: (clientX: number, clientY: number) => XY;
  clientToFlow: (clientX: number, clientY: number) => XY;
  zoomBy: (factor: number) => void;
  /** Fit every measured node, or these boxes (where nodes are headed, mid-animation). */
  fitView: (animate?: boolean, boxes?: Box[]) => void;
  goTo: (to: Viewport, animate?: boolean) => void;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
}

export function useViewport(store: CanvasStore, refs: Refs): ViewportApi {
  const vp = useRef<Viewport>(store.getState().viewport);
  const frame = useRef<number | null>(null);
  const anim = useRef<number | null>(null);
  const wheelIdle = useRef<number | null>(null);
  const pointers = useRef(new Map<number, XY>());
  const gesture = useRef<Gesture>(null);

  // Trap 7: pan and zoom never go through React. One transform on the layer,
  // and the dot pattern re-rasterised at real pixel size (never CSS-scaled).
  const paint = useCallback(() => {
    frame.current = null;
    const { x, y, zoom } = vp.current;
    const layer = refs.layer.current;
    if (layer) layer.style.transform = `translate(${x}px, ${y}px) scale(${zoom})`;
    const pattern = refs.pattern.current;
    const dot = refs.dot.current;
    if (pattern && dot) {
      const gap = GAP * zoom;
      pattern.setAttribute('width', String(gap));
      pattern.setAttribute('height', String(gap));
      pattern.setAttribute('x', String((((x % gap) + gap) % gap) - gap / 2));
      pattern.setAttribute('y', String((((y % gap) + gap) % gap) - gap / 2));
      dot.setAttribute('cx', String(gap / 2));
      dot.setAttribute('cy', String(gap / 2));
      dot.setAttribute('r', String(Math.max(0.4, DOT_R * zoom)));
    }
  }, [refs.layer, refs.pattern, refs.dot]);

  const write = useCallback(
    (next: Viewport) => {
      vp.current = next;
      if (frame.current === null) frame.current = requestAnimationFrame(paint);
    },
    [paint],
  );

  const commit = useCallback(() => {
    const cur = vp.current;
    const prev = store.getState().viewport;
    if (prev.x !== cur.x || prev.y !== cur.y || prev.zoom !== cur.zoom) store.setState({ viewport: { ...cur } });
  }, [store]);

  const stopAnimation = () => {
    if (anim.current !== null) cancelAnimationFrame(anim.current);
    anim.current = null;
  };

  /** Jump, or glide over 200ms. Reduced motion always jumps. */
  const go = useCallback(
    (to: Viewport, animate: boolean) => {
      stopAnimation();
      if (!animate || prefersReducedMotion()) {
        // Painted now, not next frame: the first fit lands in the same frame the nodes turn visible.
        if (frame.current !== null) cancelAnimationFrame(frame.current);
        vp.current = to;
        paint();
        commit();
        return;
      }
      const from = { ...vp.current };
      const t0 = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - t0) / ANIMATE_MS);
        const e = 1 - (1 - t) ** 3;
        write({
          x: from.x + (to.x - from.x) * e,
          y: from.y + (to.y - from.y) * e,
          zoom: from.zoom + (to.zoom - from.zoom) * e,
        });
        if (t < 1) anim.current = requestAnimationFrame(step);
        else {
          anim.current = null;
          commit();
        }
      };
      anim.current = requestAnimationFrame(step);
    },
    [write, commit, paint],
  );

  const panePoint = useCallback(
    (clientX: number, clientY: number): XY => {
      const r = refs.pane.current?.getBoundingClientRect();
      return { x: clientX - (r?.left ?? 0), y: clientY - (r?.top ?? 0) };
    },
    [refs.pane],
  );

  const clientToFlow = useCallback(
    (clientX: number, clientY: number) => screenToFlow(panePoint(clientX, clientY), vp.current),
    [panePoint],
  );

  const zoomBy = useCallback(
    (factor: number) => {
      const pane = refs.pane.current;
      if (!pane) return;
      const center = { x: pane.clientWidth / 2, y: pane.clientHeight / 2 };
      go(zoomAt(vp.current, center, clampZoom(vp.current.zoom * factor)), true);
    },
    [go, refs.pane],
  );

  const fitView = useCallback(
    (animate = false, boxes?: Box[]) => {
      const pane = refs.pane.current;
      if (!pane) return;
      const bounds = boundsOf(boxes ?? Object.values(store.getState().nodes).filter((b) => b.measured));
      const W = pane.clientWidth;
      const H = pane.clientHeight;
      if (!bounds || W === 0 || H === 0) {
        go({ x: 48, y: 48, zoom: 1 }, false);
        return;
      }
      go(fitViewport(bounds, W, H), animate);
    },
    [go, refs.pane, store],
  );

  const goTo = useCallback((to: Viewport, animate = false) => go(to, animate), [go]);

  // First paint before the browser draws, so the layer never shows untransformed.
  useLayoutEffect(() => {
    paint();
  }, [paint]);

  // Trap 1, passive wheel: React's onWheel is passive, so preventDefault would
  // fail and a pinch would zoom the whole page. Attached by hand, passive false.
  useEffect(() => {
    const pane = refs.pane.current;
    if (!pane) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      stopAnimation();
      const unit = e.deltaMode === 1 ? 0.05 : e.deltaMode === 2 ? 1 : 0.002;
      // A trackpad pinch arrives as ctrl+wheel with small deltas; macOS needs the boost.
      const boost = e.ctrlKey && isMac() ? 10 : 1;
      const delta = Math.max(-1, Math.min(1, -e.deltaY * unit * boost));
      const cur = vp.current;
      write(zoomAt(cur, panePoint(e.clientX, e.clientY), cur.zoom * 2 ** delta));
      if (wheelIdle.current !== null) window.clearTimeout(wheelIdle.current);
      wheelIdle.current = window.setTimeout(() => {
        wheelIdle.current = null;
        commit();
      }, WHEEL_IDLE_MS);
    };
    pane.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      pane.removeEventListener('wheel', onWheel);
      if (wheelIdle.current !== null) window.clearTimeout(wheelIdle.current);
    };
  }, [refs.pane, write, commit, panePoint]);

  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      stopAnimation();
    },
    [],
  );

  const startPinch = () => {
    const [a, b] = Array.from(pointers.current.values());
    const pa = panePoint(a.x, a.y);
    const pb = panePoint(b.x, b.y);
    gesture.current = {
      kind: 'pinch',
      vp0: { ...vp.current },
      mid0: { x: (pa.x + pb.x) / 2, y: (pa.y + pb.y) / 2 },
      dist0: Math.max(1, Math.hypot(pa.x - pb.x, pa.y - pb.y)),
    };
    refs.pane.current?.classList.add('np-panning');
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    stopAnimation();
    // The first pointer of a gesture is primary: anything still mapped is a pointer
    // released off the pane before capture, and it must not turn this into a pinch.
    if (e.isPrimary) pointers.current.clear();
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      gesture.current = { kind: 'pan', id: e.pointerId, start: { x: e.clientX, y: e.clientY }, vp0: { ...vp.current }, moved: false };
    } else if (pointers.current.size === 2) {
      // Trap 10: both fingers belong to the pane now, so neither can drag anything else.
      pointers.current.forEach((_p, id) => capture(refs.pane.current, id));
      startPinch();
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    if (g.kind === 'pan') {
      if (e.pointerId !== g.id) return;
      const dx = e.clientX - g.start.x;
      const dy = e.clientY - g.start.y;
      if (!g.moved) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
        g.moved = true;
        // Captured only once it is a pan: capturing on pointerdown would retarget
        // the click of a plain tap to the pane and a tapped node would never open.
        capture(refs.pane.current, e.pointerId);
        refs.pane.current?.classList.add('np-panning');
      }
      write({ x: g.vp0.x + dx, y: g.vp0.y + dy, zoom: g.vp0.zoom });
      return;
    }
    const pts = Array.from(pointers.current.values());
    if (pts.length < 2) return;
    const pa = panePoint(pts[0].x, pts[0].y);
    const pb = panePoint(pts[1].x, pts[1].y);
    const mid = { x: (pa.x + pb.x) / 2, y: (pa.y + pb.y) / 2 };
    const zoom = clampZoom(g.vp0.zoom * (Math.hypot(pa.x - pb.x, pa.y - pb.y) / g.dist0));
    // Keep the flow point that was under the starting midpoint under the moving one.
    const f = screenToFlow(g.mid0, g.vp0);
    write({ x: mid.x - f.x * zoom, y: mid.y - f.y * zoom, zoom });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (!pointers.current.delete(e.pointerId)) return;
    const g = gesture.current;
    if (!g) return;
    if (g.kind === 'pan' && e.pointerId !== g.id) return;
    const moved = g.kind === 'pinch' || g.moved;
    // A pinch that drops to one finger ends; the finger left behind does nothing until lifted.
    gesture.current = null;
    refs.pane.current?.classList.remove('np-panning');
    if (moved) {
      swallowNextClick();
      commit();
    }
  };

  // The pointer handlers read refs and stable callbacks only, so one set serves
  // every render and the runtime context built on this object never changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ({ vp, panePoint, clientToFlow, zoomBy, fitView, goTo, onPointerDown, onPointerMove, onPointerUp }), [
    panePoint,
    clientToFlow,
    zoomBy,
    fitView,
    goTo,
  ]);
}
